import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { baseUrl } from '../../utils/axios'
import { handleErrors } from '../../utils/helpers'
import { IPaginatedResponse } from 'types/api/general.interface'
import { toast } from 'react-toastify'
import {
  IAssignUsersToTeamRequest,
  IAssignUsersToTeamResponse,
  IBasicUserSimple,
  ICreateOperationalSiteRequest,
  ICreateOperationalTeamRequest,
  IGetOperationalSitesParams,
  IGetOperationalTeamsParams,
  IGetPreassignDataRequest,
  IGetPreassignDataResponse,
  IOperationalManagementState,
  IOperationalOverview,
  IOperationalSite,
  IOperationalSiteOrganogram,
  IOperationalSiteSimple,
  IOperationalTeam,
  IOperationalTeamDetails,
  IOperationalTeamSimple,
  IUpdateOperationalSiteRequest,
} from './operationalManagement.types'

const buildInitialState = (): IOperationalManagementState => ({
  isLoading: false,
  isLoadingSites: false,
  overview: null,
  sites: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  organogramBySiteId: {},
  loadingOrganogramSiteIds: [],
  creatingSite: false,
  updatingSite: false,
  teams: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  isLoadingTeams: false,
  creatingTeam: false,
  simpleSites: [],
  simpleTeams: [],
  simpleTeamsBySite: {},
  supervisorCandidates: [],
  teamDetailsById: {},
  loadingTeamDetailsIds: [],
  assigningEmployees: false,
})

const initialState: IOperationalManagementState = buildInitialState()

export const getOperationalOverview = createAsyncThunk(
  'operationalManagement/getOverview',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalOverview> = await baseUrl.get(
        'operational/management/overview',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationalSites = createAsyncThunk(
  'operationalManagement/getSites',
  async (params: IGetOperationalSitesParams, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IOperationalSite>> = await baseUrl.get(
        'operational/management/site',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationalSiteOrganogram = createAsyncThunk(
  'operationalManagement/getSiteOrganogram',
  async (siteId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalSiteOrganogram> = await baseUrl.get(
        `operational/management/site/${siteId}/organogram`,
      )
      return { siteId, data: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue({ siteId, error })
    }
  },
)

export const createOperationalSite = createAsyncThunk(
  'operationalManagement/createSite',
  async (body: ICreateOperationalSiteRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalSite> = await baseUrl.post(
        'operational/management/site',
        body,
      )
      toast.success('Site created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateOperationalSite = createAsyncThunk(
  'operationalManagement/updateSite',
  async (body: IUpdateOperationalSiteRequest, thunkAPI) => {
    try {
      const { id, ...payload } = body
      const resp: AxiosResponse<IOperationalSite> = await baseUrl.patch(
        `operational/management/site/${id}`,
        payload,
      )
      toast.success('Site updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationalTeams = createAsyncThunk(
  'operationalManagement/getTeams',
  async (params: IGetOperationalTeamsParams, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IOperationalTeam>> = await baseUrl.get(
        'operational/management/team',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createOperationalTeam = createAsyncThunk(
  'operationalManagement/createTeam',
  async (body: ICreateOperationalTeamRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalTeam> = await baseUrl.post(
        'operational/management/team',
        body,
      )
      toast.success('Team created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSimpleOperationalSites = createAsyncThunk(
  'operationalManagement/getSimpleSites',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalSiteSimple[]> = await baseUrl.get(
        'operational/management/site/simple',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSimpleOperationalTeams = createAsyncThunk(
  'operationalManagement/getSimpleTeams',
  async (params: { siteId?: number } | undefined, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalTeamSimple[]> = await baseUrl.get(
        'operational/management/team/simple',
        { params: params?.siteId ? { siteId: params.siteId } : undefined },
      )
      return { siteId: params?.siteId ?? null, data: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationalTeamDetails = createAsyncThunk(
  'operationalManagement/getTeamDetails',
  async (teamId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IOperationalTeamDetails> = await baseUrl.get(
        `operational/management/team/${teamId}/details`,
      )
      return { teamId, data: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue({ teamId, error })
    }
  },
)

export const getBulkAssignPreview = createAsyncThunk(
  'operationalManagement/bulkAssignPreview',
  async (body: IGetPreassignDataRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetPreassignDataResponse> = await baseUrl.post(
        'operational/management/team/preassign',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const assignUsersToTeam = createAsyncThunk(
  'operationalManagement/assignUsersToTeam',
  async (body: IAssignUsersToTeamRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAssignUsersToTeamResponse> = await baseUrl.post(
        'operational/management/team/assign',
        body,
      )
      const successful = resp.data?.successfulAssignments?.length ?? 0
      const unsuccessful = resp.data?.unsuccessfulAssignments?.length ?? 0
      if (successful > 0 && unsuccessful === 0) {
        toast.success(`Assigned ${successful} ${successful === 1 ? 'employee' : 'employees'}`)
      } else if (successful > 0) {
        toast.info(`Assigned ${successful}, skipped ${unsuccessful}`)
      } else {
        toast.error('No employees could be assigned')
      }
      return { request: body, response: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSupervisorCandidates = createAsyncThunk(
  'operationalManagement/getSupervisorCandidates',
  async (q: string | undefined, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IBasicUserSimple>> = await baseUrl.get(
        'users/basic',
        { params: { page: 1, limit: 100, state: 1, ...(q ? { q } : {}) } },
      )
      return resp.data.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const operationalManagementSlice = createSlice({
  name: 'operationalManagement',
  initialState,
  reducers: {
    clearOperationalManagement: () => buildInitialState(),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOperationalOverview.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getOperationalOverview.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.overview = payload
      })
      .addCase(getOperationalOverview.rejected, (state, { payload }) => {
        state.isLoading = false
        handleErrors(payload)
      })

      .addCase(getOperationalSites.pending, (state) => {
        state.isLoadingSites = true
      })
      .addCase(getOperationalSites.fulfilled, (state, { payload }) => {
        state.isLoadingSites = false
        state.sites = payload
      })
      .addCase(getOperationalSites.rejected, (state, { payload }) => {
        state.isLoadingSites = false
        handleErrors(payload)
      })

      .addCase(getOperationalSiteOrganogram.pending, (state, { meta }) => {
        const siteId = meta.arg
        if (typeof siteId === 'number' && !state.loadingOrganogramSiteIds.includes(siteId)) {
          state.loadingOrganogramSiteIds.push(siteId)
        }
      })
      .addCase(getOperationalSiteOrganogram.fulfilled, (state, { payload }) => {
        state.organogramBySiteId[payload.siteId] = payload.data
        state.loadingOrganogramSiteIds = state.loadingOrganogramSiteIds.filter(
          (id) => id !== payload.siteId,
        )
      })
      .addCase(getOperationalSiteOrganogram.rejected, (state, { payload, meta }) => {
        const siteId = (payload as { siteId?: number } | undefined)?.siteId ?? meta.arg
        if (typeof siteId === 'number') {
          state.loadingOrganogramSiteIds = state.loadingOrganogramSiteIds.filter(
            (id) => id !== siteId,
          )
        }
        handleErrors((payload as { error?: unknown } | undefined)?.error ?? payload)
      })

      .addCase(createOperationalSite.pending, (state) => {
        state.creatingSite = true
      })
      .addCase(createOperationalSite.fulfilled, (state, { payload }) => {
        state.creatingSite = false
        state.simpleSites.push({ id: payload.id, name: payload.name })
        state.overview = null
      })
      .addCase(createOperationalSite.rejected, (state, { payload }) => {
        state.creatingSite = false
        handleErrors(payload)
      })

      .addCase(updateOperationalSite.pending, (state) => {
        state.updatingSite = true
      })
      .addCase(updateOperationalSite.fulfilled, (state, { payload }) => {
        state.updatingSite = false
        const idx = state.sites.data.findIndex((s) => s.id === payload.id)
        if (idx !== -1) {
          state.sites.data[idx] = { ...state.sites.data[idx], ...payload }
        }
        const simpleIdx = state.simpleSites.findIndex((s) => s.id === payload.id)
        if (simpleIdx !== -1) {
          state.simpleSites[simpleIdx] = { id: payload.id, name: payload.name }
        }
        // #3: invalidate derived caches keyed on this site
        delete state.organogramBySiteId[payload.id]
        state.teams.data.forEach((team) => {
          if (team.operationalSite?.id === payload.id) {
            team.operationalSite = { id: payload.id, name: payload.name }
          }
        })
        state.overview = null
      })
      .addCase(updateOperationalSite.rejected, (state, { payload }) => {
        state.updatingSite = false
        handleErrors(payload)
      })

      .addCase(getOperationalTeams.pending, (state) => {
        state.isLoadingTeams = true
      })
      .addCase(getOperationalTeams.fulfilled, (state, { payload }) => {
        state.isLoadingTeams = false
        state.teams = payload
      })
      .addCase(getOperationalTeams.rejected, (state, { payload }) => {
        state.isLoadingTeams = false
        handleErrors(payload)
      })

      .addCase(createOperationalTeam.pending, (state) => {
        state.creatingTeam = true
      })
      .addCase(createOperationalTeam.fulfilled, (state, { payload }) => {
        state.creatingTeam = false
        state.simpleTeams.push({ id: payload.id, name: payload.name, type: payload.type })
        // Drop any stale cached details for this team id (defensive — e.g., deleted+recreated)
        delete state.teamDetailsById[payload.id]
        const siteId = payload.operationalSite?.id
        if (siteId != null) {
          const scoped = state.simpleTeamsBySite[siteId]
          if (scoped) {
            scoped.push({ id: payload.id, name: payload.name, type: payload.type })
          }
          // #4: invalidate organogram for the site the team was created in
          delete state.organogramBySiteId[siteId]
        }
        state.overview = null
      })
      .addCase(createOperationalTeam.rejected, (state, { payload }) => {
        state.creatingTeam = false
        handleErrors(payload)
      })

      .addCase(getSimpleOperationalSites.fulfilled, (state, { payload }) => {
        state.simpleSites = payload
      })
      .addCase(getSimpleOperationalSites.rejected, (_state, { payload }) => {
        handleErrors(payload)
      })

      .addCase(getSimpleOperationalTeams.fulfilled, (state, { payload }) => {
        if (payload.siteId == null) {
          state.simpleTeams = payload.data
        } else {
          state.simpleTeamsBySite[payload.siteId] = payload.data
        }
      })
      .addCase(getSimpleOperationalTeams.rejected, (_state, { payload }) => {
        handleErrors(payload)
      })

      .addCase(getSupervisorCandidates.fulfilled, (state, { payload }) => {
        state.supervisorCandidates = payload
      })
      .addCase(getSupervisorCandidates.rejected, (_state, { payload }) => {
        handleErrors(payload)
      })

      .addCase(getOperationalTeamDetails.pending, (state, { meta }) => {
        const id = meta.arg
        if (typeof id === 'number' && !state.loadingTeamDetailsIds.includes(id)) {
          state.loadingTeamDetailsIds.push(id)
        }
      })
      .addCase(getOperationalTeamDetails.fulfilled, (state, { payload }) => {
        state.teamDetailsById[payload.teamId] = payload.data
        state.loadingTeamDetailsIds = state.loadingTeamDetailsIds.filter(
          (id) => id !== payload.teamId,
        )
      })
      .addCase(getOperationalTeamDetails.rejected, (state, { payload, meta }) => {
        const teamId = (payload as { teamId?: number } | undefined)?.teamId ?? meta.arg
        if (typeof teamId === 'number') {
          state.loadingTeamDetailsIds = state.loadingTeamDetailsIds.filter(
            (id) => id !== teamId,
          )
        }
        handleErrors((payload as { error?: unknown } | undefined)?.error ?? payload)
      })

      .addCase(assignUsersToTeam.pending, (state) => {
        state.assigningEmployees = true
      })
      .addCase(assignUsersToTeam.fulfilled, (state, { payload }) => {
        state.assigningEmployees = false
        // #13: invalidate caches so the UI pulls fresh data after assigning
        const targetTeamId = payload.request.operationalTeamId
        const assignedUserIds = new Set(payload.request.userIds)

        // Resolve target site from teams list OR from previously-cached team details
        const targetSiteId =
          state.teams.data.find((t) => t.id === targetTeamId)?.operationalSite?.id ??
          state.teamDetailsById[targetTeamId]?.operationalSite?.id ??
          null

        delete state.teamDetailsById[targetTeamId]
        if (targetSiteId != null) {
          delete state.organogramBySiteId[targetSiteId]
        }

        // Only wipe cached details for teams that actually contain a reassigned user —
        // those are the only ones whose member lists are stale now.
        Object.keys(state.teamDetailsById).forEach((key) => {
          const id = Number(key)
          if (id === targetTeamId) return
          const details = state.teamDetailsById[id]
          const lostMember = details?.currentMembers.some(
            (m) => m.user != null && assignedUserIds.has(m.user.id),
          )
          if (lostMember) {
            // Also invalidate the source team's site organogram
            const sourceSiteId = details?.operationalSite?.id
            if (sourceSiteId != null) {
              delete state.organogramBySiteId[sourceSiteId]
            }
            delete state.teamDetailsById[id]
          }
        })

        state.overview = null
      })
      .addCase(assignUsersToTeam.rejected, (state, { payload }) => {
        state.assigningEmployees = false
        handleErrors(payload)
      })
  },
})

export const { clearOperationalManagement } = operationalManagementSlice.actions
export default operationalManagementSlice.reducer
